/**
 * CommandsPage - Comandos Salvos
 * Versão Premium com tokens semânticos
 */

import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Stack,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Container,
  Checkbox,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import TableChartIcon from '@mui/icons-material/TableChart';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

import { useEffectAsync } from '../reactHelper';
import { useTranslation } from '../common/components/LocalizationProvider';
import { formatBoolean } from '../common/util/formatter';
import { prefixString } from '../common/util/stringUtils';
import fetchOrThrow from '../common/util/fetchOrThrow';
import PageLayout from '../common/components/PageLayout';
import SettingsMenu from './components/SettingsMenu';
import { useManager, useRestriction } from '../common/util/permissions';
import useCrudExports from '../common/hooks/useCrudExports';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';
import { useTableSelection } from '../common/hooks';

// Componentes Premium
import CommandsTableRow from './components/CommandsTableRow';
import {
  PremiumStatCards,
  PremiumEmptyState,
  PremiumLoadingSkeleton,
  BulkActionsBar,
  BulkDeleteDialog,
} from '../common/components/premium';
import TerminalIcon from '@mui/icons-material/Terminal';
import SmsIcon from '@mui/icons-material/Sms';
import SmsFailedIcon from '@mui/icons-material/SmsFailed';

const CommandsPage = () => {
  const navigate = useNavigate();
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const manager = useManager();
  const limitCommands = useRestriction('limitCommands');

  // Estados
  const [timestamp, setTimestamp] = useState(Date.now());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Menu de exportação
  const [exportAnchorEl, setExportAnchorEl] = useState(null);

  // Bulk action states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  // Fetch de dados
  useEffectAsync(async () => {
    setLoading(true);
    try {
      const response = await fetchOrThrow('/api/commands');
      setItems(await response.json());
    } finally {
      setLoading(false);
    }
  }, [timestamp]);

  // Callbacks memoizados
  const handleFilterChange = useCallback((filter) => {
    setStatusFilter(filter);
    setPage(0);
  }, []);

  const handleRefresh = useCallback(() => {
    setTimestamp(Date.now());
  }, []);

  // Estatísticas memoizadas
  const stats = useMemo(() => {
    const total = items.length;
    const smsEnabled = items.filter((i) => i.textChannel).length;
    const smsDisabled = total - smsEnabled;
    return { total, smsEnabled, smsDisabled };
  }, [items]);

  // Filtros para StatCards (usa tokens dinâmicos)
  const statFilters = useMemo(() => [
    { value: '', label: t('sharedAll'), count: stats.total, color: tokens?.accent?.primary, Icon: TerminalIcon },
    { value: 'smsEnabled', label: t('commandSendSms') || 'SMS Ativo', count: stats.smsEnabled, color: tokens?.semantic?.success, Icon: SmsIcon },
    { value: 'smsDisabled', label: t('sharedDisabled') || 'SMS Inativo', count: stats.smsDisabled, color: tokens?.semantic?.error, Icon: SmsFailedIcon },
  ], [t, stats, tokens]);

  // Colunas do skeleton
  const skeletonColumns = useMemo(() => [
    { type: 'avatar' },
    { type: 'textShort' },
    { type: 'text' },
    { type: 'chip' },
    { type: 'chipSmall', align: 'center' },
    ...(limitCommands ? [] : [{ type: 'actions', count: 2 }]),
  ], [limitCommands]);

  // Filtragem + Busca memoizada
  const filteredItems = useMemo(() => {
    let result = items;
    if (statusFilter === 'smsEnabled') {
      result = result.filter((i) => i.textChannel);
    } else if (statusFilter === 'smsDisabled') {
      result = result.filter((i) => !i.textChannel);
    }
    if (searchText.trim()) {
      const lower = searchText.toLowerCase();
      result = result.filter(
        (i) =>
          i.description?.toLowerCase().includes(lower) ||
          i.type?.toLowerCase().includes(lower) ||
          i.id?.toString().includes(lower)
      );
    }
    return result;
  }, [items, statusFilter, searchText]);

  // Paginação memoizada
  const paginated = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredItems.slice(start, start + rowsPerPage);
  }, [filteredItems, page, rowsPerPage]);

  // Bulk selection hook
  const {
    selectedIds,
    selectedCount,
    toggleSelect,
    selectAll,
    clearSelection,
    isSelected,
    isAllSelected,
    isIndeterminate,
    hasSelection,
  } = useTableSelection(filteredItems);

  // Bulk delete handler
  const handleBulkDelete = useCallback(async () => {
    setBulkActionLoading(true);
    try {
      // Delete each selected command
      await Promise.all(
        selectedIds.map((id) =>
          fetchOrThrow(`/api/commands/${id}`, { method: 'DELETE' })
        )
      );
      clearSelection();
      setDeleteDialogOpen(false);
      setTimestamp(Date.now()); // Refresh list
    } catch (error) {
      console.error('Erro ao excluir comandos:', error);
    } finally {
      setBulkActionLoading(false);
    }
  }, [selectedIds, clearSelection]);

  // Exportações
  const exportColumns = [
    { field: 'id', headerNameKey: 'sharedId' },
    { field: 'description', headerNameKey: 'sharedDescription' },
    {
      field: 'type',
      headerNameKey: 'sharedType',
      valueGetter: ({ row }) => t(prefixString('command', row.type) || 'sharedType'),
    },
    {
      field: 'textChannel',
      headerNameKey: 'reportColumnTextChannel',
      valueGetter: ({ row }) => formatBoolean(row.textChannel, t),
    },
  ];

  const { handleExportCsv, handleExportXlsx, handleExportPdf } = useCrudExports({
    titleKey: 'reportCommandsTitle',
    headerKey: 'reportCommandsHeader',
    rows: filteredItems,
    columns: exportColumns,
    t,
    options: { includeWatermark: true },
  });

  const hasFilter = statusFilter || searchText.trim();

  return (
    <PageLayout menu={<SettingsMenu />} breadcrumbs={['settingsTitle', 'sharedSavedCommands']}>
      <Container maxWidth={false} sx={{ py: 2 }}>
        <Stack spacing={2}>

          {/* CARDS DE ESTATÍSTICAS */}
          <PremiumStatCards
            filters={statFilters}
            activeFilter={statusFilter}
            onFilterChange={handleFilterChange}
          />

          {/* BARRA DE AÇÕES */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: { xs: 1, sm: 1.5 },
              alignItems: { xs: 'stretch', md: 'center' },
              flexShrink: 0,
            }}
          >
            <TextField
              size="small"
              value={searchText}
              onChange={(e) => { setSearchText(e.target.value); setPage(0); }}
              placeholder={t('sharedSearch')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: tokens?.accent?.primary }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: 1,
                minWidth: { xs: '100%', sm: 200 },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: tokens?.surface?.glass,
                  borderRadius: tokens?.radius?.md,
                  '&:hover': {
                    backgroundColor: tokens?.surface?.hover,
                  },
                },
              }}
            />
            <Stack
              direction="row"
              spacing={0.5}
              sx={{
                flexShrink: 0,
                flexWrap: 'wrap',
                gap: 0.5,
                justifyContent: { xs: 'flex-start', md: 'flex-end' },
              }}
            >
              <Button
                variant="contained"
                color="info"
                size="small"
                startIcon={<AddCircleIcon />}
                onClick={() => navigate('/settings/command')}
                disabled={limitCommands}
                sx={{
                  backgroundColor: tokens?.accent?.primary,
                  '&:hover': { backgroundColor: tokens?.accent?.primaryHover },
                }}
              >
                {t('sharedAdd') || 'Adicionar'}
              </Button>
              {manager && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FileDownloadIcon />}
                  onClick={(e) => setExportAnchorEl(e.currentTarget)}
                  sx={{
                    borderColor: tokens?.border?.default,
                    color: tokens?.text?.secondary,
                    '&:hover': {
                      borderColor: tokens?.accent?.primary,
                      bgcolor: `${tokens?.accent?.primary}10`,
                    },
                  }}
                >
                  {t('reportExport') || 'Exportar'}
                </Button>
              )}
            </Stack>
          </Box>

          {/* Results count & Bulk Actions Bar */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
            <Typography variant="body2" color="text.secondary">
              {filteredItems.length} {t('sharedSavedCommands') || 'comandos'}
              {hasFilter && ` (${t('sharedFiltered') || 'filtrado'})`}
            </Typography>

            {hasSelection && !limitCommands && (
              <BulkActionsBar
                selectedCount={selectedCount}
                onClearSelection={clearSelection}
                onDelete={() => setDeleteDialogOpen(true)}
                loading={bulkActionLoading}
                entityName={t('sharedSavedCommands') || 'comando(s)'}
              />
            )}
          </Box>

          {/* TABELA */}
          <Paper
            className="premium-table-container"
            sx={{
              flex: 1,
              overflow: 'hidden',
              borderRadius: tokens?.radius?.lg,
              backgroundColor: tokens?.surface?.glass,
              border: `1px solid ${tokens?.border?.subtle}`,
            }}
          >
            <TableContainer sx={{ maxHeight: 'calc(100vh - 380px)' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {/* Checkbox Column */}
                    {!limitCommands && (
                      <TableCell padding="checkbox" sx={{ bgcolor: tokens?.surface?.elevated, width: 48 }}>
                        <Checkbox
                          indeterminate={isIndeterminate}
                          checked={isAllSelected}
                          onChange={selectAll}
                          sx={{
                            color: tokens?.text?.tertiary,
                            '&.Mui-checked, &.MuiCheckbox-indeterminate': {
                              color: tokens?.accent?.primary,
                            },
                          }}
                        />
                      </TableCell>
                    )}
                    <TableCell sx={{ bgcolor: tokens?.surface?.elevated, width: 48 }} />
                    <TableCell sx={{ bgcolor: tokens?.surface?.elevated, fontWeight: 600, color: tokens?.text?.secondary }}>
                      ID
                    </TableCell>
                    <TableCell sx={{ bgcolor: tokens?.surface?.elevated, fontWeight: 600, color: tokens?.text?.secondary }}>
                      {t('sharedDescription')}
                    </TableCell>
                    <TableCell sx={{ bgcolor: tokens?.surface?.elevated, fontWeight: 600, color: tokens?.text?.secondary }}>
                      {t('sharedType')}
                    </TableCell>
                    <TableCell align="center" sx={{ bgcolor: tokens?.surface?.elevated, fontWeight: 600, color: tokens?.text?.secondary }}>
                      {t('commandSendSms') || 'SMS'}
                    </TableCell>
                    {!limitCommands && (
                      <TableCell align="right" sx={{ bgcolor: tokens?.surface?.elevated, fontWeight: 600, color: tokens?.text?.secondary }}>
                        {t('sharedActions')}
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <PremiumLoadingSkeleton rows={5} columns={skeletonColumns} />
                  ) : paginated.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={limitCommands ? 5 : 7} sx={{ border: 0 }}>
                        <PremiumEmptyState
                          icon={TerminalIcon}
                          title={t('sharedNoData') || 'Nenhum comando salvo'}
                          titleFiltered={t('sharedNoResults') || 'Nenhum resultado encontrado'}
                          subtitle={t('sharedAddFirstCommand') || 'Adicione seu primeiro comando salvo'}
                          subtitleFiltered={t('sharedTryDifferentFilter') || 'Tente ajustar os filtros'}
                          hasFilter={hasFilter}
                          addPath="/settings/command"
                          addLabel={t('sharedAdd') || 'Adicionar'}
                          disabled={limitCommands}
                        />
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginated.map((row) => (
                      <CommandsTableRow
                        key={row.id}
                        row={row}
                        onRefresh={handleRefresh}
                        limitCommands={limitCommands}
                        selected={isSelected(row.id)}
                        onSelect={!limitCommands ? toggleSelect : undefined}
                      />
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredItems.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage={t('sharedRowsPerPage') || 'Linhas por página'}
              sx={{
                borderTop: `1px solid ${tokens?.border?.subtle}`,
                '.MuiTablePagination-select': {
                  color: tokens?.text?.primary,
                },
              }}
            />
          </Paper>
        </Stack>
      </Container>

      {/* MENU DE EXPORTAÇÃO */}
      <Menu
        anchorEl={exportAnchorEl}
        open={Boolean(exportAnchorEl)}
        onClose={() => setExportAnchorEl(null)}
        PaperProps={{
          sx: {
            backgroundColor: tokens?.surface?.overlay,
            backdropFilter: 'blur(16px)',
            border: `1px solid ${tokens?.border?.subtle}`,
            borderRadius: tokens?.radius?.lg,
            minWidth: 150,
          },
        }}
      >
        <MenuItem
          onClick={() => { handleExportCsv(); setExportAnchorEl(null); }}
          sx={{ gap: 1.5 }}
        >
          <FileDownloadIcon sx={{ fontSize: 18, color: tokens?.semantic?.success }} />
          CSV
        </MenuItem>
        <MenuItem
          onClick={() => { handleExportXlsx(); setExportAnchorEl(null); }}
          sx={{ gap: 1.5 }}
        >
          <TableChartIcon sx={{ fontSize: 18, color: tokens?.semantic?.info }} />
          Excel
        </MenuItem>
        <MenuItem
          onClick={() => { handleExportPdf(); setExportAnchorEl(null); }}
          sx={{ gap: 1.5 }}
        >
          <PictureAsPdfIcon sx={{ fontSize: 18, color: tokens?.semantic?.error }} />
          PDF
        </MenuItem>
      </Menu>

      {/* Bulk Delete Dialog */}
      {!limitCommands && (
        <BulkDeleteDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={handleBulkDelete}
          selectedCount={selectedCount}
          loading={bulkActionLoading}
          entityName={t('sharedSavedCommands') || 'comando(s)'}
        />
      )}
    </PageLayout>
  );
};

export default CommandsPage;
